import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Eye, Clock, Wallet, Banknote, ArrowRightLeft, FileText, X, User, Printer, Download, Loader2,
} from 'lucide-react';
import { exportSessionsToExcel } from '../../utils/excelExport';
import {
  fetchAdminSessions,
  forceCloseSessionAdmin,
  reviewSession,
} from '../../store/slices/adminSessionsSlice';
import apiService from '../../services/api';
import { isAdminOrOwner } from '../../lib/roles';
import { subscribeToSessionsUpdates } from '../../services/socket';
import { formatEGP, formatDateTime } from '../../utils/format';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { printSessionReceiptAdmin, storeInfoForSessionPrint } from '../pos/Receipt';
import { getTokens } from '../../utils/tokens';
import { getAdminSelectCSS } from '../../utils/adminSelect';
import {
  PageHeader,
  StatStrip,
  GhostBtn,
  Toolbar,
  FilterChip,
  StatusPill,
  DataTable,
  Pagination,
  MonoMoney,
  AvatarInitials,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const Sessions = () => {
  const dispatch = useDispatch();
  const { mode } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.auth);
  const { list, loading, error } = useSelector((state) => state.adminSessions);
  const { t, isRTL, lang } = useLanguage();
  const showToast = useToast();

  const tk = getTokens(mode);
  const { language } = useLanguage();
  const fontFamily = language === 'ar' ? '"Cairo", sans-serif' : '"DM Sans", sans-serif';

  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [cashiers, setCashiers] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const [forceCloseModal, setForceCloseModal] = useState({ open: false, session: null, notes: '' });
  const [reviewModal, setReviewModal] = useState({
    open: false, session: null, cash: '', notes: '',
  });
  const [viewModal, setViewModal] = useState({ open: false, session: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const hasActiveFilters =
    statusFilter !== 'all' ||
    cashierFilter !== 'all' ||
    !!startDate ||
    !!endDate;

  useEffect(() => {
    (async () => {
      try {
        const usersRes = await apiService.getUsers(true);
        const users = Array.isArray(usersRes?.data) ? usersRes.data : usersRes ?? [];
        setCashiers(users.filter((u) => u.role === 'cashier'));
      } catch (e) {
        console.error('Failed to load cashiers/registers', e);
      }
    })();
  }, []);

  const refetchSessions = () => {
    dispatch(
      fetchAdminSessions({
        status: statusFilter === 'all' ? undefined : statusFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        cashier_id: cashierFilter === 'all' ? undefined : cashierFilter,
      })
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    refetchSessions();
  }, [dispatch, statusFilter, startDate, endDate, cashierFilter]);

  useEffect(() => {
    const unsubscribe = subscribeToSessionsUpdates(refetchSessions);
    return unsubscribe;
  }, [dispatch, statusFilter, startDate, endDate, cashierFilter]);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const allSessions = useMemo(() => list || [], [list]);

  const openSessions = useMemo(
    () => allSessions.filter((s) => s.status === 'open'),
    [allSessions]
  );

  const todaySessions = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return allSessions.filter(
      (s) => s.start_time && s.start_time.split('T')[0] === today
    );
  }, [allSessions]);

  const todaySalesTotal = useMemo(
    () =>
      todaySessions.reduce(
        (sum, s) =>
          sum +
          parseFloat(s.expected_closing_cash || 0) +
          parseFloat(s.expected_closing_card || 0) +
          parseFloat(s.expected_closing_instapay || 0) +
          parseFloat(s.expected_closing_wallet || 0),
        0
      ),
    [todaySessions]
  );

  const totalVariance = useMemo(
    () =>
      allSessions.reduce(
        (sum, s) => sum + parseFloat(s.difference_cash || 0),
        0
      ),
    [allSessions]
  );

  const varianceSessions = useMemo(
    () => allSessions.filter((s) => parseFloat(s.difference_cash || 0) !== 0).length,
    [allSessions]
  );

  // ── Search filter ──────────────────────────────────────────────────────────
  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return allSessions;
    const q = searchQuery.trim();
    return allSessions.filter((s) =>
      String(s.session_number ?? '').includes(q)
    );
  }, [allSessions, searchQuery]);

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage) || 1;
  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredSessions.slice(start, start + itemsPerPage);
  }, [filteredSessions, currentPage]);

  // ── Action handlers ────────────────────────────────────────────────────────
  const handleOpenForceClose = (session) => {
    setForceCloseModal({ open: true, session, notes: '' });
  };

  const handleConfirmForceClose = async () => {
    if (!forceCloseModal.session) return;
    if (forceCloseModal.notes && forceCloseModal.notes.trim().length > 150) {
      showToast(t('alerts.forceCloseNotesMaxLength'), "warning");
      return;
    }
    setActionLoading(true);
    try {
      await dispatch(
        forceCloseSessionAdmin({
          id: forceCloseModal.session.id,
          data: { notes: forceCloseModal.notes || undefined },
        })
      ).unwrap();
      setForceCloseModal({ open: false, session: null, notes: '' });
    } catch (err) {
      showToast(typeof err === 'string' ? err : err?.message || t('alerts.failedToForceCloseSession'), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReview = (session) => {
    setReviewModal({
      open: true,
      session,
      cash: session.closing_cash != null ? String(session.closing_cash) : '',
      notes: session.reviewed_notes || '',
    });
  };

  const handleConfirmReview = async () => {
    if (!reviewModal.session) return;
    const cash = parseFloat(reviewModal.cash);
    if (Number.isNaN(cash)) {
      showToast(t('alerts.enterValidClosingCash'), "warning");
      return;
    }
    if (cash < 0) {
      showToast(t('alerts.closingCashMustBeNonNegative'), "warning");
      return;
    }
    if (reviewModal.notes && reviewModal.notes.trim().length > 250) {
      showToast(t('alerts.reviewNotesMaxLength'), "warning");
      return;
    }
    const isManager = user?.role === 'manager';
    const alreadyReviewed = reviewModal.session.status === 'reviewed';
    if (isManager && alreadyReviewed) {
      showToast(t('alerts.managersCannotEditReviewed'), "warning");
      return;
    }
    setActionLoading(true);
    try {
      await dispatch(
        reviewSession({
          id: reviewModal.session.id,
          data: {
            closing_cash: cash,
            reviewed_notes: reviewModal.notes || undefined,
          },
        })
      ).unwrap();
      setReviewModal({ open: false, session: null, cash: '', notes: '' });
    } catch (err) {
      showToast(typeof err === 'string' ? err : err?.message || t('alerts.failedToReviewSession'), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const isAdmin = isAdminOrOwner(user?.role);

  const getStatusLabel = (status) => {
    if (status === 'open') return t('sessions.open');
    if (status === 'closed') return t('sessions.closed');
    if (status === 'force_closed') return t('sessions.forceClosed');
    return t('sessions.reviewed');
  };

  const handleExport = async () => {
    if (!filteredSessions.length) return;
    setExporting(true);
    try {
      const labels = t('sessions.excelExport');
      const L =
        typeof labels === 'object' && labels !== null
          ? { ...labels, formatSessionStatus: getStatusLabel }
          : { formatSessionStatus: getStatusLabel };
      await exportSessionsToExcel(filteredSessions, 'sessions.xlsx', L);
    } catch (err) {
      console.error('Export failed', err);
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setStatusFilter('all');
    setCashierFilter('all');
    setRegisterFilter('all');
    setStartDate('');
    setEndDate('');
  };

  // ── Shared inline style helpers ────────────────────────────────────────────
  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };

  const modalBoxStyle = {
    background: tk.surface, border: `1px solid ${tk.border}`,
    borderRadius: 14, padding: 24, maxWidth: 480, width: '100%', margin: '0 16px',
  };

  const inputStyle = {
    background: tk.sunken, border: `1px solid ${tk.border}`, borderRadius: 8,
    height: 40, padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    outline: 'none', width: '100%', fontSize: 13, boxSizing: 'border-box',
  };

  const textareaStyle = {
    background: tk.sunken, border: `1px solid ${tk.border}`, borderRadius: 8,
    padding: '10px 12px', color: tk.text, fontFamily: 'inherit',
    outline: 'none', width: '100%', fontSize: 13, boxSizing: 'border-box',
    resize: 'vertical',
  };

  const primaryBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0 16px', height: 36, background: tk.accent, color: '#fff',
    border: 'none', borderRadius: 8, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
    opacity: actionLoading ? 0.6 : 1,
  };

  const dangerBtnStyle = {
    ...primaryBtnStyle,
    background: tk.danger,
  };

  const cancelBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0 16px', height: 36,
    background: 'transparent', border: `1px solid ${tk.border}`,
    color: tk.textSecondary, borderRadius: 8, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
  };

  const actionIconBtnStyle = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: 6,
    background: tk.sunken, border: `1px solid ${tk.border}`,
    color: tk.textSecondary, cursor: 'pointer',
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const cols = [
    { key: 'session_number', label: language === 'ar' ? 'رقم الجلسة' : 'Session No' },
    { key: 'start_time',    label: language === 'ar' ? 'البداية' : 'Started' },
    { key: 'end_time',      label: language === 'ar' ? 'النهاية' : 'Ended' },
    {
      key: 'cashier_name',
      label: language === 'ar'
        ? <span style={{ display: 'inline-block', paddingRight: 15 }}>الكاشير</span>
        : 'Cashier',
    },
    { key: 'sales',         label: language === 'ar' ? 'المبيعات' : 'Sales' },
    { key: 'status',        label: language === 'ar' ? 'الحالة' : 'Status' },
    { key: 'actions',       label: '' },
  ];

  const renderCell = (row, key) => {
    if (key === 'session_number') return (
      <span style={{
        fontSize: 12.5,
        fontWeight: 400,
        fontFamily: '"JetBrains Mono", monospace',
        color: tk.text,
      }}>
        {row.session_number ?? '—'}
      </span>
    );
    if (key === 'start_time') return (
      <span style={{ fontSize: 12, color: tk.textSecondary }}>
        {row.start_time ? formatDateTime(row.start_time) : '—'}
      </span>
    );
    if (key === 'end_time') return (
      <span style={{ fontSize: 12, color: tk.textSecondary, paddingInlineEnd: 12, display: 'inline-block' }}>
        {row.end_time ? formatDateTime(row.end_time) : '—'}
      </span>
    );
    if (key === 'cashier_name') return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, paddingInlineStart: 12 }}>
        <AvatarInitials name={row.cashier_name || '?'} tk={tk} size={24} />
        <span style={{ fontSize: 12 }}>{row.cashier_name || '—'}</span>
      </div>
    );
    if (key === 'sales') {
      const sales =
        parseFloat(row.expected_closing_cash || 0) +
        parseFloat(row.expected_closing_card || 0) +
        parseFloat(row.expected_closing_instapay || 0) +
        parseFloat(row.expected_closing_wallet || 0);
      return <MonoMoney amount={sales} tk={tk} size={12.5} weight={600} />;
    }
    if (key === 'status') {
      const statusMap = {
        open: language === 'ar' ? 'مفتوحة' : 'Open',
        closed: language === 'ar' ? 'مغلقة' : 'Closed',
        force_closed: language === 'ar' ? 'أُغلقت قسراً' : 'Force Closed',
        reviewed: language === 'ar' ? 'مراجعة' : 'Reviewed',
      };
      return <StatusPill tk={tk} kind={row.status}>{statusMap[row.status] || row.status}</StatusPill>;
    }
    if (key === 'actions') return (
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          onClick={() => setViewModal({ open: true, session: row })}
          style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textSecondary }}
        >
          <Eye size={13} />
        </button>
        <button
          onClick={async () => {
            try {
              const res = await apiService.getStore();
              const d = res?.data || res || {};
              printSessionReceiptAdmin(row, storeInfoForSessionPrint(d));
            } catch { printSessionReceiptAdmin(row, {}); }
          }}
          style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textSecondary }}
        >
          <Printer size={13} />
        </button>
        {isAdmin && row.status === 'open' && (
          <button
            onClick={() => handleOpenForceClose(row)}
            style={{ width: 28, height: 28, background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.dangerText }}
          >
            <X size={13} />
          </button>
        )}
        {(row.status === 'closed' ||
          row.status === 'force_closed' ||
          (isAdmin && row.status === 'reviewed')) && (
          <button
            onClick={() => handleOpenReview(row)}
            style={{ width: 28, height: 28, background: tk.accentSoft, border: `1px solid ${tk.accent}33`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.accentText }}
            title={row.status === 'reviewed' && isAdmin ? (language === 'ar' ? 'إعادة مراجعة' : 'Re-review') : undefined}
          >
            <FileText size={13} />
          </button>
        )}
      </div>
    );
    return <span style={{ color: tk.textSecondary, fontSize: 12 }}>{row[key] || '—'}</span>;
  };

  // ── Status filter options ──────────────────────────────────────────────────
  const statusOptions = [
    { value: 'all', label: t('sessions.allStatuses') },
    { value: 'open', label: t('sessions.open') },
    { value: 'closed', label: t('sessions.closed') },
    { value: 'force_closed', label: t('sessions.forceClosed') },
    { value: 'reviewed', label: t('sessions.reviewed') },
  ];

  const cashierOptions = [
    { value: 'all', label: t('sessions.allCashiers') },
    ...cashiers.map((c) => ({ value: String(c.id), label: c.full_name })),
  ];

  // ── Native select shared style helper ─────────────────────────────────────
  const filterSelect = getAdminSelectCSS(tk, 'sess-filter');

  return (
    <div
      className={`theme-${mode}`}
      style={{ background: tk.bg, color: tk.text, padding: 20, minHeight: '100%', fontFamily, overflowY: 'auto' }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <style>{filterSelect.css}</style>

      {/* Page header */}
      <PageHeader
        tk={tk}
        title={t('nav.sessions')}
        subtitle={language === 'ar' ? 'كل جلسات الكاشير المفتوحة والمغلقة' : 'All cashier sessions'}
        actions={
          <GhostBtn tk={tk} icon={exporting ? Loader2 : Download} onClick={handleExport}>
            {exporting ? t('monthlyClosing.downloading') : t('sessions.downloadExcel')}
          </GhostBtn>
        }
      />

      {/* Stat strip */}
      {loading ? (
        <StatStripSkeleton tk={tk} count={4} />
      ) : (
        <StatStrip
          tk={tk}
          items={[
            { label: language === 'ar' ? 'مفتوحة الآن' : 'Open now', value: String(openSessions.length) },
            { label: language === 'ar' ? 'جلسات اليوم' : 'Today', value: String(todaySessions.length) },
            { label: language === 'ar' ? 'مبيعات اليوم' : 'Sales today', value: todaySalesTotal, money: true },
            {
              label: language === 'ar' ? 'فروق نقدية' : 'Cash variance',
              value: Math.abs(totalVariance),
              money: true,
              delta: varianceSessions + (language === 'ar' ? ' جلسات' : ' session(s)'),
              positive: totalVariance >= 0,
            },
          ]}
        />
      )}

      {/* Toolbar — search + filters */}
      <Toolbar
        tk={tk}
        searchPlaceholder={t('sessions.searchBySessionNo')}
        searchValue={searchQuery}
        onSearchChange={(v) => setSearchQuery(v.replace(/\D/g, ''))}
        searchInputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
        filters={
          <>
            {/* Status filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 0', minWidth: 140 }}>
              <span style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('common.status')}
              </span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: '100%' }}
                className={filterSelect.className(statusFilter !== 'all')}
                data-active={filterSelect.dataActive(statusFilter !== 'all')}
              >
                <option value="all">{t('sessions.allStatuses')}</option>
                <option value="open">{t('sessions.open')}</option>
                <option value="closed">{t('sessions.closed')}</option>
                <option value="force_closed">{t('sessions.forceClosed')}</option>
                <option value="reviewed">{t('sessions.reviewed')}</option>
              </select>
            </div>

            {/* Cashier filter */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 0', minWidth: 140 }}>
              <span style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('sessions.cashier')}
              </span>
              <select
                value={cashierFilter}
                onChange={(e) => setCashierFilter(e.target.value)}
                style={{ width: '100%' }}
                className={filterSelect.className(cashierFilter !== 'all')}
                data-active={filterSelect.dataActive(cashierFilter !== 'all')}
              >
                <option value="all">{language === 'ar' ? 'كل الكاشيرين' : 'All cashiers'}</option>
                {cashiers.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>

            {/* Date range */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 0', minWidth: 190 }}>
              <span style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('common.from')}
              </span>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 8, color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 0', minWidth: 190 }}>
              <span style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('common.to')}
              </span>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', height: 34, padding: '0 10px', background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 8, color: tk.text, fontFamily: 'inherit', fontSize: 12, outline: 'none' }}
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  height: 34,
                  padding: '0 10px',
                  borderRadius: 8,
                  background: tk.dangerSoft,
                  border: 'none',
                  color: tk.dangerText,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                <X size={12} />
                {t('common.reset')}
              </button>
            )}
          </>
        }
      />

      {error && (
        <div style={{ background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, borderRadius: 8, padding: '10px 14px', color: tk.dangerText, fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[0.6, 1.5, 1.5, 1.5, 1.5, 1.5, 1, 0.7]} />
      ) : (
        <DataTable
          tk={tk}
          emptyMessage={language === 'ar' ? 'لا توجد جلسات' : 'No sessions found'}
          columns={cols}
          rows={paginatedSessions}
          renderCell={renderCell}
        />
      )}

      <Pagination
        tk={tk}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* MODALS */}

      {/* Force Close Modal */}
      {forceCloseModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, padding: 24, maxWidth: 420, width: '100%', margin: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: tk.text, margin: 0 }}>{t('sessions.forceClose')}</h3>
              <button
                onClick={() => setForceCloseModal({ open: false, session: null, notes: '' })}
                style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textMuted }}
              >
                <X size={13} />
              </button>
            </div>
            <p style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 16, marginTop: 0 }}>
              {language === 'ar'
                ? `جلسة رقم ${forceCloseModal.session?.session_number ?? '—'} — ${forceCloseModal.session?.cashier_name}`
                : `Session #${forceCloseModal.session?.session_number ?? '—'} — ${forceCloseModal.session?.cashier_name}`}
            </p>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                {t('sessions.notes')}
              </div>
              <textarea
                value={forceCloseModal.notes}
                onChange={(e) => setForceCloseModal((m) => ({ ...m, notes: e.target.value }))}
                maxLength={150}
                style={{ width: '100%', height: 80, background: tk.sunken, border: `1px solid ${tk.border}`, borderRadius: 8, padding: '8px 12px', color: tk.text, fontFamily: 'inherit', fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setForceCloseModal({ open: false, session: null, notes: '' })}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, fontSize: 13, fontFamily: 'inherit', fontWeight: 500, cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmForceClose}
                disabled={actionLoading}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: tk.danger, color: '#fff', fontSize: 13, fontFamily: 'inherit', fontWeight: 600, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.6 : 1 }}
              >
                {actionLoading ? t('common.loading') : t('sessions.forceClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {reviewModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, padding: 24, maxWidth: 620, width: '100%', margin: '0 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: tk.text, margin: 0 }}>{t('sessions.reviewSession')}</h3>
              <button
                onClick={() => setReviewModal({ open: false, session: null, cash: '', notes: '' })}
                style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textMuted }}
              >
                <X size={13} />
              </button>
            </div>
            {/* Payment breakdown (mirrors view modal style) */}
            {(() => {
              const rs = reviewModal.session || {};
              const rOpening = parseFloat(rs.opening_cash || 0);
              const rCashSales = parseFloat(rs.expected_closing_cash || 0);
              const rCardSales = parseFloat(rs.expected_closing_card || 0);
              const rInstapaySales = parseFloat(rs.expected_closing_instapay || 0);
              const rWalletSales = parseFloat(rs.expected_closing_wallet || 0);
              const rRefund = parseFloat(rs.refund_amount || 0);
              const rTotalSales = rCashSales + rCardSales + rInstapaySales + rWalletSales;
              const rExpectedCash = rOpening + rCashSales - rRefund;
              const rPaymentCols = [
                { label: t('pos.cash'), amount: rCashSales },
                { label: t('pos.card'), amount: rCardSales },
                { label: t('pos.instapay'), amount: rInstapaySales },
                { label: t('pos.wallet'), amount: rWalletSales },
              ];
              return (
                <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 14px',
                    borderBottom: `1px solid ${tk.border}`,
                  }}>
                    <span style={{ fontSize: 12.5, fontWeight: 500, color: tk.textSecondary }}>
                      {language === 'ar' ? 'النقدي الافتتاحي' : 'Opening cash'}
                    </span>
                    <MonoMoney amount={rOpening} tk={tk} size={12.5} weight={600} />
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    background: tk.surfaceAlt,
                    borderBottom: `1px solid ${tk.border}`,
                  }}>
                    {rPaymentCols.map(({ label }, i) => (
                      <div
                        key={label}
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: tk.textMuted,
                          textTransform: 'uppercase',
                          letterSpacing: 0.4,
                          textAlign: 'center',
                          padding: '8px 6px',
                          borderInlineEnd: i < rPaymentCols.length - 1 ? `1px solid ${tk.border}` : undefined,
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    borderBottom: `1px solid ${tk.border}`,
                  }}>
                    {rPaymentCols.map(({ label, amount }, i) => (
                      <div
                        key={label}
                        style={{
                          textAlign: 'center',
                          padding: '12px 6px',
                          borderInlineEnd: i < rPaymentCols.length - 1 ? `1px solid ${tk.border}` : undefined,
                        }}
                      >
                        <MonoMoney amount={amount} tk={tk} size={12.5} weight={600} />
                      </div>
                    ))}
                  </div>

                  {[
                    { label: t('sessions.totalSales'), amount: rTotalSales, bold: true, mutedBg: true },
                    { label: t('receipt.refundAmount'), amount: rRefund },
                    { label: t('sessions.expectedCash'), amount: rExpectedCash, bold: true, mutedBg: true },
                  ].map(({ label, amount, bold, mutedBg }, i, arr) => (
                    <div
                      key={label}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderBottom: i < arr.length - 1 ? `1px solid ${tk.border}` : undefined,
                        background: mutedBg ? tk.surfaceAlt : undefined,
                      }}
                    >
                      <span style={{ fontSize: 12.5, fontWeight: bold ? 600 : 500, color: bold ? tk.text : tk.textSecondary }}>
                        {label}
                      </span>
                      <MonoMoney amount={amount} tk={tk} size={12.5} weight={bold ? 700 : 600} />
                    </div>
                  ))}
                </div>
              );
            })()}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                {t('sessions.closingCash')}
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={reviewModal.cash}
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '' || raw === '.') {
                    setReviewModal((m) => ({ ...m, cash: raw }));
                    return;
                  }
                  if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
                  const n = parseFloat(raw);
                  if (Number.isNaN(n) || n < 0) return;
                  setReviewModal((m) => ({ ...m, cash: raw }));
                }}
                style={{ width: '100%', height: 40, background: tk.sunken, border: `1px solid ${tk.border}`, borderRadius: 8, padding: '0 12px', color: tk.text, fontFamily: 'inherit', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>
                {t('sessions.notes')}
              </div>
              <textarea
                value={reviewModal.notes}
                onChange={(e) => setReviewModal((m) => ({ ...m, notes: e.target.value }))}
                maxLength={250}
                style={{ width: '100%', height: 80, background: tk.sunken, border: `1px solid ${tk.border}`, borderRadius: 8, padding: '8px 12px', color: tk.text, fontFamily: 'inherit', fontSize: 13, outline: 'none', resize: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setReviewModal({ open: false, session: null, cash: '', notes: '' })}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmReview}
                disabled={actionLoading}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: tk.accent, color: '#fff', fontSize: 13, fontFamily: 'inherit', fontWeight: 600, cursor: actionLoading ? 'not-allowed' : 'pointer', opacity: actionLoading ? 0.6 : 1 }}
              >
                {actionLoading ? t('common.loading') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewModal.open && viewModal.session && (() => {
        const s = viewModal.session;
        const fmt = (v) => (v != null && v !== '' ? formatEGP(v) : '—');

        const diffColor = (v) => {
          if (v == null) return tk.textMuted;
          const n = Number(v);
          if (n === 0) return tk.successText;
          if (n > 0) return tk.warning;
          return tk.dangerText;
        };

        const getStatusText = (status) => {
          if (status === 'open') return t('sessions.open');
          if (status === 'closed') return t('sessions.closed');
          if (status === 'force_closed') return t('sessions.forceClosed');
          return t('sessions.reviewed');
        };

        const cashSales = parseFloat(s.expected_closing_cash || 0);
        const cardSales = parseFloat(s.expected_closing_card || 0);
        const instapaySales = parseFloat(s.expected_closing_instapay || 0);
        const walletSales = parseFloat(s.expected_closing_wallet || 0);
        const openingCash = parseFloat(s.opening_cash || 0);
        const refundAmount = parseFloat(s.refund_amount || 0);
        const totalSales = cashSales + cardSales + instapaySales + walletSales;
        const totalExpectedCash = openingCash + cashSales - refundAmount;
        const diff = parseFloat(s.difference_cash || 0);
        const paymentCols = [
          { label: t('pos.cash'), amount: cashSales },
          { label: t('pos.card'), amount: cardSales },
          { label: t('pos.instapay'), amount: instapaySales },
          { label: t('pos.wallet'), amount: walletSales },
        ];

        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div style={{
              background: tk.surface,
              border: `1px solid ${tk.border}`,
              borderRadius: 14,
              maxWidth: 720,
              width: '100%',
              margin: '0 16px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px 24px 16px',
                flexShrink: 0,
                borderBottom: `1px solid ${tk.border}`,
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 600, color: tk.text, margin: 0 }}>
                      {language === 'ar'
                        ? `جلسة رقم ${s.session_number ?? '—'}`
                        : `Session #${s.session_number ?? '—'}`}
                    </h3>
                    <StatusPill tk={tk} kind={s.status}>{getStatusText(s.status)}</StatusPill>
                  </div>
                  <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 2 }}>{s.cashier_name}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {isAdmin && s.status === 'reviewed' && (
                    <button
                      onClick={() => {
                        handleOpenReview(s);
                        setViewModal({ open: false, session: null });
                      }}
                      style={{
                        padding: '0 10px',
                        height: 28,
                        background: tk.accentSoft,
                        border: `1px solid ${tk.accent}33`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        color: tk.accentText,
                      }}
                    >
                      {language === 'ar' ? 'إعادة مراجعة' : 'Re-review'}
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        const res = await apiService.getStore();
                        const d = res?.data || res || {};
                        printSessionReceiptAdmin(s, storeInfoForSessionPrint(d));
                      } catch { printSessionReceiptAdmin(s, {}); }
                    }}
                    style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textMuted }}
                  >
                    <Printer size={13} />
                  </button>
                  <button
                    onClick={() => setViewModal({ open: false, session: null })}
                    style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textMuted }}
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 24px' }}>

              {/* Meta grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                {[
                  { label: language === 'ar' ? 'البداية' : 'Start', val: s.start_time ? formatDateTime(s.start_time) : '—' },
                  { label: language === 'ar' ? 'النهاية' : 'End', val: s.end_time ? formatDateTime(s.end_time) : '—' },
                  { label: language === 'ar' ? 'النقدي الافتتاحي' : 'Opening cash', val: null, money: parseFloat(s.opening_cash || 0) },
                  {
                    label: t('sessions.closingCash'),
                    val: s.closing_cash != null ? null : '—',
                    money: s.closing_cash != null ? parseFloat(s.closing_cash) : undefined,
                  },
                ].map(({ label, val, money }, i) => (
                  <div key={i} style={{ background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
                    {money !== undefined
                      ? <MonoMoney amount={money} tk={tk} size={14} weight={600} />
                      : <div style={{ fontSize: 13, fontWeight: 500, color: tk.text }}>{val}</div>
                    }
                  </div>
                ))}
              </div>

              {/* Payment breakdown */}
              <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  background: tk.surfaceAlt,
                  borderBottom: `1px solid ${tk.border}`,
                }}>
                  {paymentCols.map(({ label }, i) => (
                    <div
                      key={label}
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: tk.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        textAlign: 'center',
                        padding: '8px 6px',
                        borderInlineEnd: i < paymentCols.length - 1 ? `1px solid ${tk.border}` : undefined,
                      }}
                    >
                      {label}
                    </div>
                  ))}
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  borderBottom: `1px solid ${tk.border}`,
                }}>
                  {paymentCols.map(({ label, amount }, i) => (
                    <div
                      key={label}
                      style={{
                        textAlign: 'center',
                        padding: '12px 6px',
                        borderInlineEnd: i < paymentCols.length - 1 ? `1px solid ${tk.border}` : undefined,
                      }}
                    >
                      <MonoMoney amount={amount} tk={tk} size={12.5} weight={600} />
                    </div>
                  ))}
                </div>

                {[
                  { label: t('sessions.totalSales'), amount: totalSales, bold: true, mutedBg: true },
                  { label: t('receipt.refundAmount'), amount: refundAmount },
                  { label: t('sessions.expectedCash'), amount: totalExpectedCash, bold: true, mutedBg: true },
                ].map(({ label, amount, bold, mutedBg }) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '10px 14px',
                      borderBottom: `1px solid ${tk.border}`,
                      background: mutedBg ? tk.surfaceAlt : undefined,
                    }}
                  >
                    <span style={{ fontSize: 12.5, fontWeight: bold ? 600 : 500, color: bold ? tk.text : tk.textSecondary }}>
                      {label}
                    </span>
                    <MonoMoney amount={amount} tk={tk} size={12.5} weight={bold ? 700 : 600} />
                  </div>
                ))}

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 14px',
                  background: diff !== 0 ? (diff < 0 ? tk.successSoft : tk.dangerSoft) : tk.surfaceAlt,
                }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: tk.text }}>
                    {t('sessions.difference')}
                  </span>
                  <MonoMoney
                    amount={Math.abs(diff)}
                    tk={tk}
                    size={12.5}
                    weight={700}
                    color={diff < 0 ? tk.successText : diff > 0 ? tk.dangerText : tk.textSecondary}
                  />
                </div>
              </div>

              {/* Notes & review info */}
              {(s.closed_by_name || s.reviewed_by_name || s.notes || s.reviewed_at || s.reviewed_notes) && (
                <div style={{ background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <User size={12} /> {t('common.actions')} & {t('sessions.notes')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {s.closed_by_name && (
                      <div>
                        <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 2 }}>{t('sessions.closeSession')}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{s.closed_by_name}</div>
                      </div>
                    )}
                    {s.reviewed_by_name && (
                      <div>
                        <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 2 }}>{t('sessions.reviewedBy')}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{s.reviewed_by_name}</div>
                      </div>
                    )}
                    {s.reviewed_at && (
                      <div>
                        <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 2 }}>{t('sessions.reviewed')}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: tk.text }}>{formatDateTime(s.reviewed_at)}</div>
                      </div>
                    )}
                    {s.notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 2 }}>{t('sessions.notes')}</div>
                        <div style={{ fontSize: 12.5, color: tk.textSecondary }}>{s.notes}</div>
                      </div>
                    )}
                    {s.reviewed_notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 2 }}>{t('sessions.reviewNotes')}</div>
                        <div style={{ fontSize: 12.5, color: tk.textSecondary }}>{s.reviewed_notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {s.notes && !s.closed_by_name && !s.reviewed_by_name && !s.reviewed_at && !s.reviewed_notes && (
                <div style={{ background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 8, padding: '10px 12px', fontSize: 12, color: tk.textSecondary }}>
                  <strong style={{ color: tk.text }}>{t('sessions.notes')}:</strong> {s.notes}
                </div>
              )}

              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '11px 24px 13px',
                flexShrink: 0,
                borderTop: `1px solid ${tk.border}`,
              }}>
                <button
                  onClick={() => setViewModal({ open: false, session: null })}
                  style={{ padding: '6px 17px', borderRadius: 8, border: `1px solid ${tk.border}`, background: 'transparent', color: tk.textSecondary, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer' }}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default Sessions;
